import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { Settings, Calculator, Activity, CornerUpLeft, Filter, RefreshCw, BarChart2, Layers, CheckCircle2 } from 'lucide-react';
import { calculateCharge, ELEMENTS, DEFAULT_MATERIALS } from '../utils/alloyCalculatorEngine';
import './AlloyCalculator.css';

const DEFAULT_TARGETS = {
    Cu: 85, Sn: 5, Pb: 5, Zn: 5, Fe: 0, Sb: 0, Ni: 0,
    Al: 0, Mn: 0, Si: 0, S: 0, P: 0, Bi: 0, Nb: 0
};

const DEFAULT_CURRENT = {
    Cu: 82, Sn: 4, Pb: 4, Zn: 4, Fe: 0.1, Sb: 0, Ni: 0,
    Al: 0, Mn: 0, Si: 0, S: 0, P: 0, Bi: 0, Nb: 0
};

const DEFAULT_RECOVERY = {
    Cu: 99, Sn: 95, Pb: 98, Zn: 90, Fe: 100, Sb: 100, Ni: 100,
    Al: 80, Mn: 95, Si: 90, S: 100, P: 100, Bi: 100, Nb: 100
};

const AlloyCalculator = () => {
    const navigate = useNavigate();
    const userModule = localStorage.getItem('userModule') || 'CU';

    const [chargeWeight, setChargeWeight] = useState(1000);
    const [currentComp, setCurrentComp] = useState(DEFAULT_CURRENT);
    const [targetComp, setTargetComp] = useState(DEFAULT_TARGETS);
    const [recoveryFact, setRecoveryFact] = useState(DEFAULT_RECOVERY);
    const [activeTab, setActiveTab] = useState('current'); // 'current', 'target', 'recovery'

    const [results, setResults] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);

    const handleInputChange = (setter, el, val) => {
        setter(prev => ({ ...prev, [el]: val === '' ? '' : Number(val) }));
    };

    const handleCalculate = () => {
        setIsCalculating(true);
        setTimeout(() => {
            const res = calculateCharge(chargeWeight, currentComp, targetComp, recoveryFact, DEFAULT_MATERIALS);
            setResults(res);
            setIsCalculating(false);
        }, 800);
    };

    const renderInputsList = (data, setter) => {
        return (
            <div className="element-grid">
                {ELEMENTS.map(el => (
                    <div key={el} className="element-input">
                        <label>{el} (%)</label>
                        <input
                            type="number"
                            className="input-field"
                            value={data[el] ?? ''}
                            onChange={(e) => handleInputChange(setter, el, e.target.value)}
                            step="0.01"
                            min="0"
                            max="100"
                        />
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="alloy-container">
            <nav className="alloy-nav glass-panel rounded-none">
                <div className="nav-brand" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <img src={userModule === 'FE' ? "ladle-logic-iron.png" : "logo.png"} className="nav-logo" alt="Logo" />
                    <h2 className="text-gradient" style={{ margin: 0 }}>Ladle Logic - Charge Calculator</h2>
                </div>
                <div className="nav-actions">
                    <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
                        <CornerUpLeft size={18} /> Back to Dashboard
                    </button>
                </div>
            </nav>

            <div className="alloy-content">
                {/* LEFTSIDE: Input Parameters */}
                <div className="input-sidebar glass-panel">
                    <div className="alloy-section">
                        <h3 className="m-0"><Settings size={18} className="text-primary" /> Base Parameters</h3>
                        <div className="element-input">
                            <label>Initial Charge Weight (lbs)</label>
                            <input
                                type="number"
                                className="input-field"
                                value={chargeWeight}
                                onChange={(e) => setChargeWeight(Number(e.target.value))}
                            />
                        </div>
                    </div>

                    <div className="alloy-section mt-4">
                        <h3 className="m-0"><Filter size={18} className="text-primary" /> Composition Settings</h3>

                        <div className="flex gap-2 mb-2 bg-slate-100 p-1 rounded-lg">
                            <button
                                className={`flex-1 py-1 px-2 rounded-md text-sm font-semibold transition-all ${activeTab === 'current' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:bg-slate-200'}`}
                                onClick={() => setActiveTab('current')}
                            >
                                Current
                            </button>
                            <button
                                className={`flex-1 py-1 px-2 rounded-md text-sm font-semibold transition-all ${activeTab === 'target' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:bg-slate-200'}`}
                                onClick={() => setActiveTab('target')}
                            >
                                Target
                            </button>
                            <button
                                className={`flex-1 py-1 px-2 rounded-md text-sm font-semibold transition-all ${activeTab === 'recovery' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:bg-slate-200'}`}
                                onClick={() => setActiveTab('recovery')}
                            >
                                Recovery
                            </button>
                        </div>

                        <div className="scrollable" style={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto', paddingRight: '10px' }}>
                            {activeTab === 'current' && renderInputsList(currentComp, setCurrentComp)}
                            {activeTab === 'target' && renderInputsList(targetComp, setTargetComp)}
                            {activeTab === 'recovery' && renderInputsList(recoveryFact, setRecoveryFact)}
                        </div>
                    </div>

                    <button
                        className="btn-primary w-full mt-auto"
                        onClick={handleCalculate}
                        disabled={isCalculating}
                    >
                        {isCalculating ? <RefreshCw className="spin" size={20} /> : <Activity size={20} />}
                        {isCalculating ? 'Computing Mix...' : 'Run Calculator Engine'}
                    </button>
                </div>

                {/* RIGHTSIDE: Results Workspace */}
                <div className="results-workspace glass-panel">
                    {!results ? (
                        <div className="h-full w-full flex flex-col items-center justify-center text-slate-400">
                            <Layers size={64} className="mb-4 opacity-50" color="#94a3b8" />
                            <h3 className="text-slate-500 text-xl mb-2">Awaiting Formulation</h3>
                            <p className="text-center max-w-md">Enter your current melt composition and desired target parameters, then run the calculator engine to generate the material addition recipe.</p>
                        </div>
                    ) : (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col gap-6 h-full pb-4">

                            {/* Summary Header */}
                            <div className="results-header">
                                <div>
                                    <h2 className="text-primary m-0 flex items-center gap-2"><CheckCircle2 size={24} color="#10b981" /> Formulated Recipe</h2>
                                    <p className="text-secondary text-sm m-0">Algorithm converged successfully over {results.iterations.length} iterations.</p>
                                </div>
                            </div>

                            {/* KPI Cards */}
                            <div className="results-grid">
                                <div className="stat-card glass-panel flex-row justify-between items-center rounded-xl">
                                    <div>
                                        <span className="stat-label">Initial Weight</span>
                                        <div className="stat-value">{results.initialWeight} <span className="text-sm text-slate-400">lbs</span></div>
                                    </div>
                                    <div className="w-px h-12 bg-slate-200"></div>
                                    <div>
                                        <span className="stat-label">Added Material</span>
                                        <div className="stat-value text-indigo-500">+{results.totalMaterialAdded} <span className="text-sm text-indigo-300">lbs</span></div>
                                    </div>
                                    <div className="w-px h-12 bg-slate-200"></div>
                                    <div>
                                        <span className="stat-label">Final Gross Yield</span>
                                        <div className="stat-value text-emerald-500">{results.finalWeight} <span className="text-sm text-emerald-300">lbs</span></div>
                                    </div>
                                </div>

                                <div className="stat-card glass-panel rounded-xl flex-row overflow-x-auto gap-4 items-center">
                                    <span className="stat-label whitespace-nowrap mr-2">Ingredients:</span>
                                    {results.materialTotals.length > 0 ? results.materialTotals.map((mat, i) => (
                                        <div key={i} className="flex flex-col items-center px-4 py-2 bg-slate-50 rounded-lg border border-slate-100 min-w-[max-content]">
                                            <span className="font-bold text-slate-700">{mat.name}</span>
                                            <span className="text-xs text-slate-500">{mat.qty} lbs</span>
                                        </div>
                                    )) : (
                                        <span className="text-emerald-500 flex items-center font-bold text-sm"><CheckCircle2 size={16} className="mr-1" /> Within Tolerance (No Addition Needed)</span>
                                    )}
                                </div>
                            </div>

                            {/* Iteration Breakdown */}
                            <div className="flex-1 min-h-[250px] flex flex-col gap-4">
                                <h3 className="m-0 text-slate-700 flex items-center gap-2"><BarChart2 size={18} className="text-indigo-500" /> Addition Steps</h3>
                                {results.iterations.length === 0 ? (
                                    <div className="bg-slate-50 p-4 border border-slate-200 rounded-lg text-slate-500 text-sm text-center">
                                        Current composition is already completely within the target tolerance.
                                    </div>
                                ) : (
                                    <div className="data-table-container custom-scrollbar max-h-[300px]">
                                        <table className="data-table">
                                            <thead>
                                                <tr>
                                                    <th>Step</th>
                                                    <th>Material Added</th>
                                                    <th>Quantity (lbs)</th>
                                                    {ELEMENTS.filter(el => results.iterations[0].compositionAfter[el] > 0 || currentComp[el] > 0 || targetComp[el] > 0).map(el => (
                                                        <th key={el} className="text-center">{el} %</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {results.iterations.map((iter, idx) => (
                                                    <tr key={idx}>
                                                        <td className="font-bold text-indigo-500">{iter.iteration}</td>
                                                        <td className="font-medium text-slate-700">{iter.materialAdded}</td>
                                                        <td className="font-mono">{iter.quantityAddedLbs} lbs</td>
                                                        {ELEMENTS.filter(el => iter.compositionAfter[el] > 0 || currentComp[el] > 0 || targetComp[el] > 0).map(el => (
                                                            <td key={el} className="text-center font-mono text-sm text-slate-600">
                                                                {iter.compositionAfter[el]}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            {/* Final Analysis */}
                            <div className="flex flex-col gap-4 mt-auto pt-4 border-t border-slate-100">
                                <h3 className="m-0 text-slate-700 flex items-center gap-2"><Activity size={18} className="text-emerald-500" /> Final Composition Deltas</h3>
                                <div className="data-table-container">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Element</th>
                                                <th>Target %</th>
                                                <th>Calculated %</th>
                                                <th>Variance</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {results.comparison.filter(c => c.target > 0 || c.calculated > 0 || c.difference !== "0.00").map((comp, idx) => {
                                                const diff = parseFloat(comp.difference);
                                                const isGood = Math.abs(diff) <= 0.05;

                                                return (
                                                    <tr key={idx}>
                                                        <td className="font-bold">{comp.element}</td>
                                                        <td className="font-mono text-slate-500">{comp.target}</td>
                                                        <td className="font-mono font-bold text-slate-800">{comp.calculated}</td>
                                                        <td className={`font-mono ${diff > 0 ? 'diff-positive' : diff < 0 ? 'diff-negative' : 'text-slate-400'}`}>
                                                            {diff > 0 ? '+' : ''}{comp.difference}
                                                        </td>
                                                        <td>
                                                            {isGood ? (
                                                                <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-xs font-bold">IN SPEC</span>
                                                            ) : (
                                                                <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded text-xs font-bold">WARNING</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AlloyCalculator;
